'use client';

import { useMemo, useState } from 'react';
import Link from '~/(tenant)/_components/tenant-link';
import { useTranslation } from 'react-i18next';

import { Instagram, Phone, ChevronDown, ChevronUp } from 'lucide-react';

import { formatTimeToAmPm } from '@kit/shared/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { Button } from '@kit/ui/button';
import { Card, CardContent } from '@kit/ui/card';
import { cn } from '@kit/ui/utils';

import type { CoachInfo } from '../_lib/server/coaches-page.loader';
import type { CoachSlot } from './coaches-list';

interface CoachCardProps {
  coach: CoachInfo;
  slots: CoachSlot[];
  selectedDate?: Date;
  locationSlug?: string | null;
  bookingOnly?: boolean;
}

function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Mirrors isPostMidnight in booking-form.tsx so post-midnight slots are
// rendered as a labelled group below regular evening slots — without this,
// a flat alphanumeric sort puts "00:00" before "23:00" and the picker reads
// "12 AM, 1 AM, … 11 PM."
function isPostMidnight(time24: string): boolean {
  const [hours] = time24.split(':').map(Number);
  return (hours ?? 0) >= 0 && (hours ?? 0) < 5;
}

export function CoachCard({ coach, slots, selectedDate, locationSlug, bookingOnly = false }: CoachCardProps) {
  const { t, i18n } = useTranslation(['coaches', 'bookings']);
  const locale = i18n.language;
  const [selectedSlot, setSelectedSlot] = useState<CoachSlot | null>(null);
  const [bioExpanded, setBioExpanded] = useState(false);
  const hasSlots = slots.length > 0;

  // When the parent updates `slots` (e.g. the user picks a different date),
  // validate that the currently-selected slot still exists in the new list.
  // If it doesn't, treat it as deselected so the booking URL is never built
  // from a slot that belongs to a different date.
  const validatedSlot = useMemo(() => {
    if (!selectedSlot) return null;
    const stillPresent = slots.some(
      (s) => s.startTime === selectedSlot.startTime && s.templateId === selectedSlot.templateId,
    );
    return stillPresent ? selectedSlot : null;
  }, [selectedSlot, slots]);

  const instagramUrl = coach.instagramHandle
    ? `https://instagram.com/${encodeURIComponent(coach.instagramHandle)}`
    : null;

  // Build booking link with human-readable params (no UUIDs exposed)
  const bookingHref = useMemo(() => {
    if (!validatedSlot) return null;

    const params: Record<string, string> = {
      type: 'trainer',
      activity: validatedSlot.sportSlug,
      duration: String(validatedSlot.duration),
      time: validatedSlot.startTime,
      coach: coach.name,
    };

    if (selectedDate) {
      const y = selectedDate.getFullYear();
      const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const d = String(selectedDate.getDate()).padStart(2, '0');
      params.date = `${y}-${m}-${d}`;
    }

    if (locationSlug) {
      params.location = locationSlug;
    }

    return `/book?${new URLSearchParams(params).toString()}`;
  }, [validatedSlot, selectedDate, locationSlug, coach.name]);

  return (
    <Card id={`coach-${coach.id}`} className="scroll-mt-24">
      <CardContent className="p-5">
        {/* Coach info */}
        {!bookingOnly && <div className="flex items-start gap-4">
          <Avatar className="h-20 w-20 shrink-0 border-2 border-primary/20">
            <AvatarImage src={coach.pictureUrl ?? undefined} alt={coach.name} />
            <AvatarFallback className="text-lg">
              {getInitials(coach.name)}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold">
              <Link href={`/coaches/${coach.slug}`} className="hover:underline">
                {coach.name}
              </Link>
            </h3>
            {coach.profileDetails?.title && (
              <p className="mt-1 text-sm font-medium">{coach.profileDetails.title}</p>
            )}

            {/* Contact info */}
            <div className="mt-1 flex flex-wrap items-center gap-3">
              {coach.phoneNumber && (
                <a
                  href={`tel:${coach.phoneNumber}`}
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:underline"
                  aria-label={t('coaches:callCoach', 'Call {{name}}', { name: coach.name })}
                >
                  <Phone className="h-3.5 w-3.5" aria-hidden />
                  {coach.phoneNumber}
                </a>
              )}
              {instagramUrl && (
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:underline"
                  aria-label={t('coaches:coachOnInstagram', '{{name}} on Instagram', { name: coach.name })}
                >
                  <Instagram className="h-3.5 w-3.5" aria-hidden />
                  @{coach.instagramHandle}
                </a>
              )}
            </div>

            {/* Bio */}
            {coach.bio && (
              <div className="mt-2">
                <p
                  className={cn(
                    'text-sm text-muted-foreground',
                    !bioExpanded && 'line-clamp-2',
                  )}
                >
                  {coach.bio}
                </p>
                {coach.bio.length > 100 && (
                  <button
                    type="button"
                    className="mt-1 flex items-center gap-1 text-xs text-primary hover:underline"
                    onClick={() => setBioExpanded(!bioExpanded)}
                  >
                    {bioExpanded ? (
                      <>
                        <ChevronUp className="h-3 w-3" aria-hidden />
                        {t('showLess', 'Show less')}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3" aria-hidden />
                        {t('readMore', 'Read more')}
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>}

        {/* Available time slots */}
        {hasSlots && (() => {
          const regularSlots = slots.filter((s) => !isPostMidnight(s.startTime));
          const postMidnightSlots = slots.filter((s) => isPostMidnight(s.startTime));

          const renderSlotButton = (slot: CoachSlot) => {
            const isSelected = validatedSlot?.startTime === slot.startTime;
            return (
              <button
                key={slot.startTime}
                type="button"
                aria-pressed={isSelected}
                onClick={() => setSelectedSlot(isSelected ? null : slot)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                  isSelected
                    ? 'bg-foreground text-white border-foreground'
                    : 'bg-background hover:bg-muted',
                )}
              >
                {formatTimeToAmPm(slot.startTime, locale)}
              </button>
            );
          };

          return (
            <div className="mt-4">
              <p className="mb-2 text-sm font-medium">
                {t('selectTime', 'Select Time')}
              </p>
              {regularSlots.length > 0 && (
                <div
                  className="flex flex-wrap gap-2"
                  role="group"
                  aria-label={t('selectTime', 'Select Time')}
                >
                  {regularSlots.map(renderSlotButton)}
                </div>
              )}
              {postMidnightSlots.length > 0 && (
                <div className="mt-4">
                  <div className="mb-3 flex items-center gap-2">
                    <div className="h-px flex-1 bg-border" />
                    <span className="px-2 text-sm font-medium text-muted-foreground">
                      {t('bookings:postMidnight', 'Post Midnight')}
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <div
                    className="flex flex-wrap gap-2"
                    role="group"
                    aria-label={t('bookings:postMidnight', 'Post Midnight')}
                  >
                    {postMidnightSlots.map(renderSlotButton)}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Book button — only rendered when a valid slot for the current date is selected */}
        {validatedSlot && bookingHref && (
          <div className="mt-4">
            <Button asChild className="w-full">
              <Link href={bookingHref}>
                {t('bookNow', 'Book Now')}
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
